import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useIntl, history } from 'umi';
import { Layout, Card, Justify, PagingQuery } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import Visualis from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/components/Visualis';
import Tables from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/components/Tables';
import SearchRecentWechat from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/SearchRecentWechat';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import '@/pages/intelligence/assets/scss/StoreRanks/index.scss';
import type { IBaseProps, IGameType } from '@/pages/intelligence/types/common';
import type {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type {
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreRankPrimaryKey,
  IStoreRankMoreCoreRankUnifiedKey,
  IStoreRankMoreCoreRank,
  IStoreRankMoreTableDimensionInfos,
} from '@/pages/intelligence/types/storeRank';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import { AppMagicGenreTagType } from '@/pages/intelligence/components/TableComponents/components/AppMagicGenreTagList';

const { Content } = Layout;
interface IStoreRankMoreCoreProps extends IBaseProps {
  entityType: IGameType;
  searchType:
  | 'mobile-store-rank-more'
  | 'pc-store-rank-more'
  | 'console-store-rank-more'
  | 'weixin-store-rank-more'
  | 'douyin-store-rank-more';
  isSectional: boolean;
  isShowVisualis: boolean;
  isOperate: boolean;
  title?: string | React.ReactElement;
  filter?: React.ReactElement;
  exportTable?: React.ReactElement;
  pageLogInfo?: any;
  fetchParams: IStoreRankMoreCoreFetchParams;

  visualisOptions: {
    isVisualisLoading: boolean;
    isVisualisNoData: boolean;
    seriesData: any[];
    xAxisData: any[];
    sortKeyName: string;
    currentLine: any;
    segments: any[];
    segment: string;
    setSegment: React.Dispatch<React.SetStateAction<string>>;
    yAxisFormatter: (payload: any) => string;
    tooltipFormatter: (payload: any) => string;
    createLegendsCustomMade: any;
  };
  tablesOptions: {
    isTableLoading: boolean;
    isTableNoData: boolean;
    imageKey: string;
    primaryKey: IStoreRankMoreCoreRankPrimaryKey;
    uunifiedKey: IStoreRankMoreCoreRankUnifiedKey;
    sectionalRankKeys: string[];
    sectionalRankInfos: {
      [key: string]: IStoreRankMoreTableDimensionInfos;
    };
    platformTopMetrics: string[];
    platformTopInfos: {
      [key: string]: IStoreRankMoreTableDimensionInfos;
    };
    tableDimensionsData: string[];
    tableTotal: number;
    tableData: IStoreRankMoreCoreRank[];
    tableSelection: TableSelectionType[];
    defaultSelectionGames: any[];
    getTableResultList: any;
    handleClearSearch: any;
    gameColumnsWithCur?: any;
    // 判断表格内 details按钮是否禁用、以及禁用时的提示文案
    getDetailsBtnDisabledInfo?: () => {
      disabled?: boolean;
      disabledMsg?: string | React.ReactElement;
    };
    onTableSortChage: (payload: SortByItemType) => void;
    onTablePagingChange: (payload: PagingQuery) => void;
    onTableCheckGame: (selection: TableSelectionType[], checkStatus: TableSelectStatusType) => void;
  };
}

const WebchatStoreRankMoreCore: React.FC<IStoreRankMoreCoreProps> = (props) => {
  const {
    entityType = 'mobile',
    searchType,
    isSectional,
    isShowVisualis,
    isOperate,
    title,
    filter,
    exportTable,
    pageLogInfo,
    isHasDetailsColumns = true,
    isHasReleaseTimeColumns = true,
    isHasGenreColumns = true,
    isWeChat = true,
    fetchParams,
    visualisOptions,
    tablesOptions,
    searchDataList,
    pageSortAttr,
  } = props;

  const {
    isVisualisLoading,
    isVisualisNoData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,
  } = visualisOptions ?? {};

  const {
    isTableLoading,
    imageKey,
    primaryKey,
    uunifiedKey,
    sectionalRankKeys = [],
    sectionalRankInfos = {},
    platformTopMetrics = [],
    platformTopInfos = {},
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    getTableResultList,
    handleClearSearch,
    getDetailsBtnDisabledInfo,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    gameColumnsWithCur,
  } = tablesOptions ?? {};

  const { formatMessage } = useIntl();

  const isVisualisLoadingTmp = isSectional ? isTableLoading : isVisualisLoading;
  const isVisualisNoDataTmp = isSectional
    ? !(tableData?.length > 0 && tableSelection?.length > 0)
    : isVisualisNoData;

  const getLeftSearchDom = useCallback(() => {
    return (
      <SearchRecentWechat
        searchType={searchType}
        pageLogInfo={pageLogInfo}
        fetchParams={{ ...fetchParams, page: 1 }}
        initSearchInput={!(fetchParams?.search_ids?.length > 0)}
        isFrontEndSearch={true}
        searchDataList={searchDataList}
        getTableResultList={getTableResultList}
        handleClear={handleClearSearch}
        searchKey={'rank_keyword'}
      />
    );
  }, [searchType, fetchParams, searchDataList]);

  return (
    <Content
      className={classnames('dbi-mobileglobalregion-more dbi-storeranks-more dbi-webchat-storeranks-more', {
        'is-sectional': isSectional,
        // mobile store rank下钻页 勾选多国家sectional模式时 表格没有勾选交互，其余情况表格都有勾选交互
        'table-selectable': !(entityType === 'mobile' && isSectional),
        'dbg-douyin-store-rank-more': !isWeChat,
      })}
    >
      <Content.Header
        title={title}
        showBackButton
        onBackButtonClick={() => {
          history.goBack();
        }}
      />
      <Content.Body full>
        {/* 筛选器 */}
        {React.isValidElement(filter) && filter}
        {isShowVisualis && (
          <Card className="dbi-mobileGames-card-context">
            <Card.Body
              title={
                <div style={{ display: 'flex' }}>
                  <span style={{ marginRight: 10 }}>{title}</span>
                  {!isTableLoading && !isVisualisNoDataTmp && (
                    <ArrowdownText
                      isNew={true}
                      sortedData={sortKeyName}
                      order={fetchParams?.order}
                    />
                  )}
                </div>
              }
            >
              <Abnormal
                isLoading={isVisualisLoadingTmp}
                isNoData={false}
                component={
                  <Visualis
                    entityType={entityType}
                    chartType={isSectional ? ChartType.Bar : ChartType.Line}
                    titleNow={formatMessage({ id: 'dbi_rank' })}
                    isLoading={isVisualisLoadingTmp}
                    isNoData={isVisualisNoDataTmp}
                    isShowSortInfo={!isTableLoading}
                    dateType={fetchParams?.granularity}
                    order={fetchParams?.order}
                    seriesData={seriesData}
                    xAxisData={xAxisData}
                    yAxisFormatter={yAxisFormatter}
                    tooltipFormatter={tooltipFormatter}
                    createLegendsCustomMade={createLegendsCustomMade}
                    currentLine={currentLine}
                    segments={segments}
                    segment={segment}
                    setSegment={setSegment}
                    imageKey={imageKey}
                    primaryKey={primaryKey}
                    sectionalRankKeys={sectionalRankKeys}
                    sortKeyName={sortKeyName}
                    tableSelection={tableSelection}
                    tooltipCfg={{
                      headerLeftTitle: fetchParams?.start_date || fetchParams?.end_date,
                    }}
                  />
                }
              />
            </Card.Body>
          </Card>
        )}
        <Justify
          className="dbi-card-justify"
          left={getLeftSearchDom()}
          right={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: '14px', marginRight: '10px', color: '#838697' }}>
                {formatMessage({ id: 'dbi_store_rank_download_tips_title' })}
              </span>
              {/* 下载按钮 */}
              {React.isValidElement(exportTable) && exportTable}
            </div>
          }
        />
        <CardSlot>
          <Tables
            entityType={entityType}
            topChartsName={fetchParams?.topchart_name}
            isSectional={isSectional}
            isSelectable={isShowVisualis}
            isOperate={isOperate}
            primaryKey={primaryKey}
            uunifiedKey={uunifiedKey}
            sectionalRankKeys={sectionalRankKeys}
            sectionalRankInfos={sectionalRankInfos}
            platformTopMetrics={platformTopMetrics}
            platformTopInfos={platformTopInfos}
            tableDimensionsData={tableDimensionsData}
            records={tableData}
            loading={isTableLoading}
            tableTotal={tableTotal}
            isHasDetailsColumns={isHasDetailsColumns}
            isHasReleaseTimeColumns={isHasReleaseTimeColumns}
            isHasGenreColumns={isHasGenreColumns}
            pageSortAttr={pageSortAttr}
            onTableSortChage={onTableSortChage}
            onTablePagingChange={onTablePagingChange}
            onTableCheckGame={onTableCheckGame}
            tableSelection={tableSelection}
            tableSelectionAll={defaultSelectionGames}
            genreSource={fetchParams?.genre_source ?? 'annie'}
            getDetailsBtnDisabledInfo={getDetailsBtnDisabledInfo}
            gameColumnsWithCur={gameColumnsWithCur}
          />
        </CardSlot>
      </Content.Body>
    </Content>
  );
};

export default WebchatStoreRankMoreCore;
